import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import {
  isInvoiceFailed,
  PaymentStatus,
  ServiceInvoiceType,
} from "@redotech/redo-billing-common/service-invoice";
import FilterIcon from "@redotech/redo-icons/filter-lines_filled.svg";
import RefreshIcon from "@redotech/redo-icons/refresh-cw-01.svg";
import StatusIcon from "@redotech/redo-icons/status.svg";
import TagIcon from "@redotech/redo-icons/tag-01.svg";
import UsersIcon from "@redotech/redo-icons/users-01.svg";
import { toast } from "@redotech/redo-web/alert";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import {
  RedoFilterDropdownAnchor,
  RedoFilterGroup,
} from "@redotech/redo-web/arbiter-components/filter-group/redo-filter-group";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import {
  RedoTable,
  RedoTableEmptyStateContent,
  RedoTableLoadingStateContent,
  type RedoTableColumn,
} from "@redotech/redo-web/arbiter-components/tables/redo-table";
import { RedoTableTextCell } from "@redotech/redo-web/arbiter-components/tables/redo-table-cells";
import { StandardRedoTableHeaderCell } from "@redotech/redo-web/arbiter-components/tables/redo-table-header-cells";
import { RedoTablePagination } from "@redotech/redo-web/arbiter-components/tables/redo-table-pagination";
import { SimpleMultiSelectFilterDropdown } from "@redotech/redo-web/filters/simple-multiselect-filter-dropdown";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { identity } from "@redotech/util/function";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { useDebounce } from "usehooks-ts";

import type {
  ListServiceInvoicesCursor,
  ListServiceInvoicesRow,
} from "@redotech/admin-sdk/rpc/schema/service-invoices/list-service-invoices";

import { ServiceInvoiceStatus } from "@redotech/admin-sdk/rpc/schema/service-invoices/service-invoice-status";
import { useTriggerLoad } from "@redotech/react-util/load";
import { useSyncedState } from "@redotech/react-util/use-synced-state";
import { RedoAdminRpcClientContext } from "../../app/redo-admin-rpc-client-provider";
import { TeamMultiSelect } from "../../team/team/shared/team-multi-select";
import {
  buildListServiceInvoicesInput,
  deserializeServiceInvoiceFilters,
  serializeServiceInvoiceFilters,
  SERVICE_INVOICE_OWNED_KEYS,
} from "./filters";

const FILTER_DEBOUNCE_MS = 1000;

const SERVICE_INVOICE_FILTERS_SESSION_KEY = "admin-service-invoices-filters";

const STATUS_LABELS: Record<ServiceInvoiceStatus, string> = {
  [ServiceInvoiceStatus.PAID]: "Paid",
  [ServiceInvoiceStatus.FAILED]: "Failed",
  [ServiceInvoiceStatus.PAYMENT_PENDING]: "Payment pending",
};

enum TableFilter {
  Status = "status",
  Product = "product",
  Team = "team",
}

const FILTER_LABELS: Record<TableFilter, string> = {
  [TableFilter.Status]: "Status",
  [TableFilter.Product]: "Product kind",
  [TableFilter.Team]: "Team",
};

const filterOptions: RedoListItem<TableFilter>[] = [
  {
    type: "text",
    id: TableFilter.Status,
    value: TableFilter.Status,
    text: FILTER_LABELS[TableFilter.Status],
    leadingItem: { type: "icon", Icon: StatusIcon },
  },
  {
    type: "text",
    id: TableFilter.Product,
    value: TableFilter.Product,
    text: FILTER_LABELS[TableFilter.Product],
    leadingItem: { type: "icon", Icon: TagIcon },
  },
  {
    type: "text",
    id: TableFilter.Team,
    value: TableFilter.Team,
    text: FILTER_LABELS[TableFilter.Team],
    leadingItem: { type: "icon", Icon: UsersIcon },
  },
];

export const ServiceInvoicesPage = memo(function ServiceInvoicesPage() {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);

  const [filters, updateFilters] = useSyncedState({
    sessionStorageKey: SERVICE_INVOICE_FILTERS_SESSION_KEY,
    serialize: serializeServiceInvoiceFilters,
    deserialize: deserializeServiceInvoiceFilters,
    ownedKeys: SERVICE_INVOICE_OWNED_KEYS,
  });
  const { statuses, kinds, teamIds } = filters;

  const setStatuses = useHandler((next: ServiceInvoiceStatus[]) => {
    updateFilters({ statuses: next });
  });
  const setKinds = useHandler((next: ServiceInvoiceType[]) => {
    updateFilters({ kinds: next });
  });
  const setTeamIds = useHandler((next: string[]) => {
    updateFilters({ teamIds: next });
  });

  const [selectedFilters, setSelectedFilters] = useState<TableFilter[]>(() => {
    const initial: TableFilter[] = [TableFilter.Status];
    if (kinds.length > 0) {
      initial.push(TableFilter.Product);
    }
    if (teamIds.length > 0) {
      initial.push(TableFilter.Team);
    }
    return initial;
  });
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const [filtersDropdownButtonRef, setFiltersDropdownButtonRef] =
    useState<HTMLButtonElement | null>(null);

  const debouncedStatuses = useDebounce(statuses, FILTER_DEBOUNCE_MS);
  const debouncedKinds = useDebounce(kinds, FILTER_DEBOUNCE_MS);
  const debouncedTeamIds = useDebounce(teamIds, FILTER_DEBOUNCE_MS);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [reachedEnd, setReachedEnd] = useState(false);
  const [totalRows, setTotalRows] = useState<number | undefined>(undefined);

  /** Used to allow jumping to arbitrary known-to-exist pages.
   * Each entry is the nextCursor returned when we fetched that page.
   * The first element is the nextCursor obtained from the first page,
   * which is used to fetch the second page. */
  const nextPageCursorsRef = useRef<(ListServiceInvoicesCursor | null)[]>([]);

  useEffect(() => {
    setCurrentPage(1);
    setReachedEnd(false);
    setTotalRows(undefined);
    nextPageCursorsRef.current = [];
  }, [debouncedStatuses, debouncedKinds, debouncedTeamIds, pageSize]);

  const [listLoad, triggerListLoad] = useTriggerLoad(async (signal) => {
    const cursor = nextPageCursorsRef.current[currentPage - 2];
    if (cursor === null) {
      return;
    }

    const result = await rpcClient.listServiceInvoices(
      buildListServiceInvoicesInput({
        cursor: cursor ?? undefined,
        limit: pageSize,
        statuses: debouncedStatuses,
        kinds: debouncedKinds,
        teamIds: debouncedTeamIds,
      }),
      { signal },
    );

    if (!result.nextCursor) {
      setReachedEnd(true);
    }
    if (currentPage > nextPageCursorsRef.current.length) {
      setTotalRows((rows) => (rows ?? 0) + result.rows.length);
      nextPageCursorsRef.current.push(result.nextCursor);
    }
    return result;
  });

  useEffect(triggerListLoad, [
    triggerListLoad,
    rpcClient,
    currentPage,
    pageSize,
    debouncedStatuses,
    debouncedKinds,
    debouncedTeamIds,
  ]);

  const rows = listLoad.value?.rows ?? [];
  const isLoading = listLoad.pending;

  const [pendingRebill, setPendingRebill] =
    useState<ListServiceInvoicesRow | null>(null);
  const [rebilling, setRebilling] = useState(false);

  const onRebillClick = useHandler((row: ListServiceInvoicesRow) => {
    setPendingRebill(row);
  });

  const closeConfirmation = useHandler(() => {
    if (rebilling) {
      return;
    }
    setPendingRebill(null);
  });

  const confirmRebill = useHandler(async () => {
    if (!pendingRebill) {
      return;
    }
    setRebilling(true);
    try {
      const result = await rpcClient.rebillServiceInvoice({
        invoiceId: pendingRebill.id,
      });
      toast(`Rebill succeeded — payment status: ${result.paymentStatus}`, {
        variant: "success",
      });
      setPendingRebill(null);
      triggerListLoad();
    } catch (e) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      toast(`Rebill failed: ${(e as Error).message}`, { variant: "error" });
    } finally {
      setRebilling(false);
    }
  });

  const columns = useMemo<RedoTableColumn<ListServiceInvoicesRow>[]>(
    () => [
      {
        key: "billingDueDate",
        renderHeaderCell: () => (
          <StandardRedoTableHeaderCell headerText="Billing due" />
        ),
        renderNormalCell: ({ row }) => (
          <RedoTableTextCell text={formatDate(row.billingDueDate)} />
        ),
      },
      {
        key: "team",
        renderHeaderCell: () => (
          <StandardRedoTableHeaderCell headerText="Team" />
        ),
        renderNormalCell: ({ row }) => (
          <RedoTableTextCell
            subtext={row.teamId}
            text={row.teamName ?? "(unnamed)"}
          />
        ),
      },
      {
        key: "product",
        renderHeaderCell: () => (
          <StandardRedoTableHeaderCell headerText="Product" />
        ),
        renderNormalCell: ({ row }) => (
          <RedoTableTextCell text={row.kinds.join(", ")} />
        ),
      },
      {
        key: "status",
        renderHeaderCell: () => (
          <StandardRedoTableHeaderCell headerText="Status" />
        ),
        renderNormalCell: ({ row }) => {
          const badge = statusBadgeFor(row);
          return <RedoBadge color={badge.color} size="sm" text={badge.text} />;
        },
      },
      {
        key: "amount",
        renderHeaderCell: () => (
          <StandardRedoTableHeaderCell headerText="Amount" />
        ),
        renderNormalCell: ({ row }) => (
          <RedoTableTextCell text={formatDollars(row.totalPriceCents)} />
        ),
      },
      {
        key: "attempts",
        renderHeaderCell: () => (
          <StandardRedoTableHeaderCell headerText="Attempts" />
        ),
        renderNormalCell: ({ row }) => (
          <RedoTableTextCell text={String(row.billingAttempts)} />
        ),
      },
      {
        key: "actions",
        renderHeaderCell: () => (
          <StandardRedoTableHeaderCell headerText="Actions" />
        ),
        renderNormalCell: ({ row }) => {
          if (!isInvoiceFailed(row)) {
            return <RedoTableTextCell text="—" />;
          }
          return (
            <RedoButton
              hierarchy="secondary"
              onClick={(e) => {
                e.stopPropagation();
                onRebillClick(row);
              }}
              size="xs"
              text="Rebill"
            />
          );
        },
      },
    ],
    [onRebillClick],
  );

  const selectedFilterOptions = useMemo<RedoListItem<TableFilter>[]>(
    () =>
      selectedFilters.map((filter) => ({
        id: filter,
        type: "text",
        text: FILTER_LABELS[filter],
        value: filter,
      })),
    [selectedFilters],
  );

  const setSelectedFilterItems = useHandler(
    (items: RedoListItem<TableFilter>[]) => {
      const nextFilters = items.map((i) => i.value);
      setSelectedFilters(nextFilters);
      if (!nextFilters.includes(TableFilter.Status)) {
        setStatuses([]);
      }
      if (!nextFilters.includes(TableFilter.Product)) {
        setKinds([]);
      }
      if (!nextFilters.includes(TableFilter.Team)) {
        setTeamIds([]);
      }
    },
  );

  const renderFilter = (filter: TableFilter) => {
    switch (filter) {
      case TableFilter.Status:
        return (
          <StatusFilter
            key="status"
            removeFilter={() => {
              setStatuses([]);
              setSelectedFilters((prev) =>
                prev.filter((f) => f !== TableFilter.Status),
              );
            }}
            setValues={setStatuses}
            values={statuses}
          />
        );
      case TableFilter.Product:
        return (
          <ProductFilter
            key="product"
            removeFilter={() => {
              setKinds([]);
              setSelectedFilters((prev) =>
                prev.filter((f) => f !== TableFilter.Product),
              );
            }}
            setValues={setKinds}
            values={kinds}
          />
        );
      case TableFilter.Team:
        return (
          <TeamMultiSelect
            connectiveText="is any of"
            key="team"
            label={FILTER_LABELS[TableFilter.Team]}
            onTeamsChange={setTeamIds}
            openOnRender={teamIds.length === 0}
            placeholder="Search teams..."
            removeFilter={() => {
              setTeamIds([]);
              setSelectedFilters((prev) =>
                prev.filter((f) => f !== TableFilter.Team),
              );
            }}
            selectedTeamIds={teamIds}
            variant="filter"
          />
        );
    }
  };

  return (
    <Flex bgColor="primary" dir="column" flex={1} gap="none" height="full">
      <Flex dir="column" gap="lg" p="3xl" shrink={0} w="full">
        <Flex align="center" dir="row" justify="flex-end" w="full">
          <RedoButton
            hierarchy="secondary"
            IconLeading={RefreshIcon}
            onClick={() => void triggerListLoad()}
            pending={isLoading}
            size="sm"
            text="Refresh"
          />
        </Flex>
        <Flex dir="row" gap="sm">
          <RedoButton
            hierarchy="secondary"
            IconLeading={FilterIcon}
            onClick={() => setFilterDropdownOpen(!filterDropdownOpen)}
            ref={setFiltersDropdownButtonRef}
            size="sm"
            text="Filters"
          />
          <RedoMultiselectDropdown
            dropdownAnchor={filtersDropdownButtonRef}
            dropdownOpen={filterDropdownOpen}
            items={filterOptions}
            selectedItems={selectedFilterOptions}
            setDropdownOpen={setFilterDropdownOpen}
            setSelectedItems={setSelectedFilterItems}
          />
        </Flex>
        {selectedFilters.length > 0 && (
          <Flex dir="row" gap="sm" wrap="wrap">
            {selectedFilters.map((filter) => renderFilter(filter))}
          </Flex>
        )}
      </Flex>

      <Flex bgColor="primary" dir="column" flex={1} overflow="auto">
        <RedoTable<ListServiceInvoicesRow>
          emptyStateContent={
            !isLoading && rows.length === 0 ? (
              <RedoTableEmptyStateContent text="No service invoices match these filters." />
            ) : undefined
          }
          layout={{ columns, headerColor: "grey", standalone: true }}
          loadingStateContent={
            isLoading && rows.length === 0 ? (
              <RedoTableLoadingStateContent />
            ) : undefined
          }
          rowsOfData={rows}
        />
      </Flex>

      <RedoTablePagination
        currentPage={currentPage}
        knownRowCount={totalRows ?? 0}
        loading={isLoading}
        pageSize={pageSize}
        setCurrentPage={setCurrentPage}
        setPageSize={setPageSize}
        trueTotalKnown={reachedEnd}
      />

      {pendingRebill && (
        <RedoModal
          isOpen
          modalSize="sm"
          onModalCloseRequested={closeConfirmation}
          primaryButton={{
            text: rebilling ? "Rebilling…" : "Rebill",
            onClick: confirmRebill,
            disabled: rebilling,
            pending: rebilling,
            theme: "destructive",
          }}
          secondaryButton={{
            text: "Cancel",
            onClick: closeConfirmation,
            disabled: rebilling,
          }}
          title="Rebill service invoice?"
        >
          <Flex dir="column" gap="md">
            <Text>
              This will charge the team again for invoice{" "}
              <strong>{pendingRebill.id}</strong> (
              {pendingRebill.kinds.join(", ")}).
            </Text>
            <Text textColor="secondary">
              Amount: {formatDollars(pendingRebill.totalPriceCents)} · Team:{" "}
              {pendingRebill.teamName ?? pendingRebill.teamId}
            </Text>
          </Flex>
        </RedoModal>
      )}
    </Flex>
  );
});

function formatDollars(cents: number): string {
  const dollars = cents / 100;
  return dollars.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
  });
}

function formatDate(d: Date): string {
  return new Date(d).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function statusBadgeFor(row: ListServiceInvoicesRow): {
  text: string;
  color: "success" | "error" | "warning" | "gray";
} {
  if (isInvoiceFailed(row)) {
    return { text: "Failed", color: "error" };
  }
  if (row.paymentStatus === PaymentStatus.PENDING) {
    return { text: "Payment pending", color: "warning" };
  }
  if (row.billedSuccessfully) {
    return { text: "Paid", color: "success" };
  }
  return { text: "Unknown", color: "gray" };
}

const StatusFilter = memo(function StatusFilter({
  values,
  setValues,
  removeFilter,
}: {
  values: ServiceInvoiceStatus[];
  setValues: (values: ServiceInvoiceStatus[]) => void;
  removeFilter: () => void;
}) {
  return (
    <RedoFilterGroup
      Icon={StatusIcon}
      propertyName="Status"
      query={<RedoFilterDropdownAnchor text="is any of" />}
      removeFilter={removeFilter}
      value={
        <SimpleMultiSelectFilterDropdown<ServiceInvoiceStatus>
          options={Object.values(ServiceInvoiceStatus)}
          optionToFriendlyName={(opt) => STATUS_LABELS[opt]}
          selectedOptionsToDisplay={(selected) => {
            if (!selected || selected.length === 0) {
              return "...";
            }
            return selected.map((s) => STATUS_LABELS[s]).join(", ");
          }}
          setValues={setValues}
          values={values}
        />
      }
    />
  );
});

const KIND_OPTIONS = Object.values(ServiceInvoiceType) as ServiceInvoiceType[];

const ProductFilter = memo(function ProductFilter({
  values,
  setValues,
  removeFilter,
}: {
  values: ServiceInvoiceType[];
  setValues: (values: ServiceInvoiceType[]) => void;
  removeFilter: () => void;
}) {
  return (
    <RedoFilterGroup
      Icon={TagIcon}
      propertyName="Product kind"
      query={<RedoFilterDropdownAnchor text="is any of" />}
      removeFilter={removeFilter}
      value={
        <SimpleMultiSelectFilterDropdown<ServiceInvoiceType>
          options={KIND_OPTIONS}
          optionToFriendlyName={identity}
          selectedOptionsToDisplay={(selected) => {
            if (!selected || selected.length === 0) {
              return "...";
            }
            return selected.join(", ");
          }}
          setValues={setValues}
          values={values}
        />
      }
    />
  );
});
